import { defineStore } from 'pinia'
import { readonly, ref } from 'vue'

import api from '@/client/core/api'
import {
  TGetInstantwinSerialCode,
  TGetInstantwinSerialCodeParams,
  TInstantwinSerialCode
} from '@/typings/api/twitter_instantwin_serial_codes.type'

export type TwitterInstantwinSerialCodeState = {
  campaignId: number
  messageId: number
  loading: boolean
  isCreated: boolean
  serialCode: {
    loaded: boolean
    list: TInstantwinSerialCode[]
    total: number
    countAssign: number
    countUnassign: number
    params: TGetInstantwinSerialCodeParams
  }
}

const createInitialState = (): TwitterInstantwinSerialCodeState => ({
  campaignId: 0,
  messageId: 0,
  loading: true,
  isCreated: false,
  serialCode: {
    loaded: false,
    list: [],
    total: 0,
    countAssign: 0,
    countUnassign: 0,
    params: {
      campaignId: 0,
      messageId: 0,
      filter: '',
      sortBy: '-status',
      pagingNo: 1,
      limitCount: 25
    }
  }
})

export const useTwitterInstantwinSerialCodeStore = defineStore('twitterInstantwinSerialCodeStore', () => {
  const initialState = createInitialState()

  const campaignId = ref<TwitterInstantwinSerialCodeState['campaignId']>(initialState.campaignId)
  const messageId = ref<TwitterInstantwinSerialCodeState['messageId']>(initialState.messageId)
  const loading = ref<TwitterInstantwinSerialCodeState['loading']>(initialState.loading)
  const isCreated = ref<TwitterInstantwinSerialCodeState['isCreated']>(initialState.isCreated)
  const serialCode = ref<TwitterInstantwinSerialCodeState['serialCode']>(initialState.serialCode)

  const reset = async (): Promise<void> => {
    const nextState = createInitialState()
    campaignId.value = nextState.campaignId
    messageId.value = nextState.messageId
    loading.value = nextState.loading
    isCreated.value = nextState.isCreated
    serialCode.value = nextState.serialCode
  }

  const setCampaignId = (newCampaignId: number): void => {
    campaignId.value = newCampaignId
    serialCode.value.params.campaignId = newCampaignId
  }

  const setMessageId = (newMessageId: number): void => {
    messageId.value = newMessageId
    serialCode.value.params.messageId = newMessageId
  }

  const setLoading = (newLoading: boolean): void => {
    loading.value = newLoading
  }

  const setIsCreated = (newIsCreated: boolean): void => {
    isCreated.value = newIsCreated
  }

  const fetchSerialCodeList = async (): Promise<void> => {
    if (!campaignId.value) {
      return
    }

    loading.value = true

    const serial: TGetInstantwinSerialCode = await api.get('/twitter_instantwin_serial_codes', {
      params: serialCode.value.params
    })

    loading.value = false

    if (!serial.data) {
      return
    }

    serialCode.value.list = serial.data.serialCode ?? []
    serialCode.value.total = serial.data.dataCount ?? 0
    serialCode.value.countAssign = serial.data.count.assign ?? 0
    serialCode.value.countUnassign = serial.data.count.unassign ?? 0
  }

  const setSerialCodeListParams = (newParams: Partial<TGetInstantwinSerialCodeParams>): void => {
    serialCode.value.params = {
      ...serialCode.value.params,
      ...newParams
    }
  }

  return {
    campaignId: readonly(campaignId),
    messageId: readonly(messageId),
    loading: readonly(loading),
    isCreated: readonly(isCreated),
    serialCode: readonly(serialCode),
    reset,
    setCampaignId,
    setMessageId,
    setLoading,
    setIsCreated,
    fetchSerialCodeList,
    setSerialCodeListParams
  }
})
